import { useState, useMemo } from "react";
import { useQuery } from "@apollo/client";
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Chip,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Stack,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import RiskAssessmentTable from "../../../../Risks/RiskManagement/features/risk-assessment/RiskAssessmentTable";
import TableSkeleton from "../../../../../components/Skeletons/TableSkeleton";
import {
  GET_ALL_RISK_ASSESSMENTS,
  GET_RISK_ASSESSMENT_ITEMS_BY_ID,
  GET_AUDIT_RISK_ASSESSMENTS,
  GET_AUDIT_RISK_ASSESSMENT_ITEMS_BY_ID,
} from "../../../../../apis/organization-apis/risk-apis/riskApis";
import { formatDate, getStartOfDayUTC, getEndOfDayUTC, formatDateForInput } from "../../../../../utils/dateUtils";
import { AssessmentStatus } from "../../../../../utils/enums";
import RiskDetailsDialog from "../Dialogs/RiskDetailsDialog";

const RiskAssessmentsTab = ({ audit, isAuditorView = false }) => {
  const theme = useTheme();
  const [selectedAssessmentIdx, setSelectedAssessmentIdx] = useState(0);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);

  // Calculate 12-month cutoff date from audit_end_date for backend filtering
  const { start_date, end_date } = useMemo(() => {
    if (!audit?.audit_end_date) return { start_date: null, end_date: null };

    const endDate = new Date(audit.audit_end_date);

    const cutoffDate = new Date(endDate);
    cutoffDate.setMonth(cutoffDate.getMonth() - 12);

    // Convert to ISO string for backend using utility functions
    return {
      start_date: getStartOfDayUTC(formatDateForInput(cutoffDate)),
      end_date: audit.audit_end_date,
    };
  }, [audit?.audit_end_date]);

  // Fetch risk assessments with backend filtering
  const { data: assessmentsData, loading: assessmentsLoading } = useQuery(
    isAuditorView ? GET_AUDIT_RISK_ASSESSMENTS : GET_ALL_RISK_ASSESSMENTS,
    {
      variables: {
        ...(isAuditorView && { audit_id: audit?.id }),
        start_date,
        end_date,
      },
      skip: !start_date || !end_date || (isAuditorView && !audit?.id),
      fetchPolicy: "cache-and-network",
    }
  );

  // Get filtered assessments directly from backend - only show completed
  const filteredAssessments = useMemo(() => {
    const allAssessments = isAuditorView
      ? assessmentsData?.getAuditRiskAssessments?.data || []
      : assessmentsData?.getAllRiskAssessments?.data || [];
    return allAssessments.filter(
      (assessment) => assessment.status_title === "Completed"
    );
  }, [assessmentsData, isAuditorView]);

  const currentAssessment = filteredAssessments[selectedAssessmentIdx];

  // Fetch risks for selected assessment
  const {
    data: risksData,
    loading: risksLoading,
    refetch: refetchRisks,
  } = useQuery(
    isAuditorView
      ? GET_AUDIT_RISK_ASSESSMENT_ITEMS_BY_ID
      : GET_RISK_ASSESSMENT_ITEMS_BY_ID,
    {
      variables: {
        ...(isAuditorView && { audit_id: audit?.id }),
        assessment_id: currentAssessment?.id,
      },
      skip: !currentAssessment?.id || (isAuditorView && !audit?.id),
    }
  );

  const risks = useMemo(() => {
    return isAuditorView
      ? risksData?.getAuditRiskAssessmentItemsById?.data || []
      : risksData?.getRiskAssessmentItemsById?.data || [];
  }, [risksData, isAuditorView]);

  const handleViewDetails = (risk) => {
    setSelectedRisk(risk);
    setDetailsDialogOpen(true);
  };

  const handleDetailsDialogClose = () => {
    setDetailsDialogOpen(false);
    setSelectedRisk(null);
  };

  const getIconComponent = (name) => {
    if (name === "Schedule") return <ScheduleIcon sx={{ fontSize: 16 }} />;
    if (name === "Check") return <CheckCircleIcon sx={{ fontSize: 16 }} />;
    return null;
  };

  const getStatusChip = (statusTitle) => {
    const status = (statusTitle || "").toLowerCase();

    if (status === AssessmentStatus.DRAFT) {
      return {
        label: "Draft",
        color: theme.customColors?.status?.warning?.text || "#D97706",
        bg: theme.customColors?.status?.warning?.bg || "#FEF3C7",
        icon: "Schedule",
      };
    } else if (status === AssessmentStatus.COMPLETED) {
      return {
        label: "Completed",
        color: theme.customColors?.status?.success?.text || "#059669",
        bg: theme.customColors?.status?.success?.bg || "#D1FAE5",
        icon: "Check",
      };
    } else if (status === AssessmentStatus.REJECTED) {
      return {
        label: "Rejected",
        color: theme.customColors?.status?.error?.text || "#DC2626",
        bg: theme.customColors?.status?.error?.bg || "#FEE2E2",
        icon: null,
      };
    }

    return {
      label: statusTitle || "Unknown",
      color: theme.palette.text.secondary,
      bg: theme.palette.grey[200],
      icon: null,
    };
  };

  const renderStatusMessage = () => {
    const status = (currentAssessment?.status_title || "").toLowerCase();

    if (status === AssessmentStatus.COMPLETED) {
      return (
        <Box>
          <Typography
            variant="h5"
            fontWeight={600}
            sx={{ color: theme.customColors?.text?.strong || "#111827" }}
          >
            Assessment approved at{" "}
            {formatDate(currentAssessment?.completed_at)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.customColors?.text?.secondary || "#6B7280",
              mt: 0.5,
            }}
          >
            Started at {formatDate(currentAssessment?.created_at)}
          </Typography>
        </Box>
      );
    }

    if (status === AssessmentStatus.REJECTED) {
      return (
        <Box>
          <Typography
            variant="h5"
            fontWeight={600}
            sx={{ color: theme.customColors?.text?.strong || "#111827" }}
          >
            Assessment discarded at {formatDate(currentAssessment?.updated_at)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.customColors?.text?.secondary || "#6B7280",
              mt: 0.5,
            }}
          >
            Started at {formatDate(currentAssessment?.created_at)}
          </Typography>
        </Box>
      );
    }

    return (
      <Typography
        variant="h5"
        fontWeight={600}
        sx={{ color: theme.customColors?.text?.strong || "#111827" }}
      >
        Assessment started at {formatDate(currentAssessment?.created_at)}
      </Typography>
    );
  };

  if (assessmentsLoading) {
    return (
      <Card
        sx={{
          minHeight: "600px",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #E8EAED",
          overflow: "hidden",
        }}
        variant="outlined"
      >
        <CardHeader
          title={
            <Typography
              variant="h5"
              sx={{ fontSize: "18px", fontWeight: "bold" }}
            >
              Risk Assessments
            </Typography>
          }
          sx={{
            borderBottom: "1px solid #ddd",
            background: "#F7F7FC",
            padding: "14px",
          }}
        />
        <CardContent sx={{ p: 0 }}>
          <TableSkeleton />
        </CardContent>
      </Card>
    );
  }

  if (filteredAssessments.length === 0) {
    return (
      <Card
        sx={{
          minHeight: "600px",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #E8EAED",
          overflow: "hidden",
        }}
        variant="outlined"
      >
        <CardHeader
          title={
            <Typography
              variant="h5"
              sx={{ fontSize: "18px", fontWeight: "bold" }}
            >
              Risk Assessments
            </Typography>
          }
          sx={{
            borderBottom: "1px solid #ddd",
            background: "#F7F7FC",
            padding: "14px",
          }}
        />
        <CardContent>
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography
              variant="body2"
              sx={{ color: theme.customColors?.text?.secondary || "#6B7280" }}
            >
              No risk assessments found within the audit period.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const statusChip = getStatusChip(currentAssessment?.status_title);

  return (
    <Card
      sx={{
        minHeight: "600px",
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        border: "1px solid #E8EAED",
        overflow: "hidden",
      }}
      variant="outlined"
    >
      <CardHeader
        title={
          <Typography
            variant="h5"
            sx={{ fontSize: "18px", fontWeight: "bold" }}
          >
            Risk Assessments
          </Typography>
        }
        sx={{
          borderBottom: "1px solid #ddd",
          background: "#F7F7FC",
          padding: "14px",
        }}
      />
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={2.25}>
          {/* Assessment Dropdown */}
          <FormControl sx={{ minWidth: 320, mb: 2 }} size="small">
            <InputLabel id="risk-assessment-select-label">
              Select Assessment
            </InputLabel>
            <Select
              labelId="risk-assessment-select-label"
              value={selectedAssessmentIdx}
              label="Select Assessment"
              onChange={(e) => setSelectedAssessmentIdx(e.target.value)}
            >
              {filteredAssessments.map((assessment, idx) => (
                <MenuItem key={assessment?.id || idx} value={idx}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.customColors?.text?.strong || "#111827",
                      }}
                    >
                      {assessment?.title} - {formatDate(assessment?.created_at)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          theme.customColors?.text?.secondary || "#6B7280",
                      }}
                    >
                      {assessment?.status_title}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Assessment Status Card */}
          {currentAssessment && (
            <Card
              variant="outlined"
              sx={{
                mb: 2,
                backgroundColor:
                  theme.customColors?.background?.cardHeader || "#F9FAFB",
                border: `1px solid ${theme.customColors?.border?.subtle || "#E5E7EB"}`,
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    {renderStatusMessage()}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color:
                            theme.customColors?.text?.secondary || "#6B7280",
                        }}
                      >
                        Created by:{" "}
                        {currentAssessment?.assessor_email || "-"}
                      </Typography>
                    </Box>
                    {currentAssessment?.status_title?.toLowerCase() ===
                      AssessmentStatus.COMPLETED && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mt: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              theme.customColors?.text?.secondary || "#6B7280",
                          }}
                        >
                          Approved by:{" "}
                          {currentAssessment?.approver_email || "-"}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Chip
                    label={statusChip.label}
                    icon={getIconComponent(statusChip.icon)}
                    size="small"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      borderRadius: 1,
                      border: "none",
                      minWidth: 90,
                      textAlign: "center",
                      backgroundColor: statusChip.bg,
                      color: statusChip.color,
                      "& .MuiChip-icon": {
                        color: "inherit",
                        fontSize: 16,
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Risks Table */}
          {risksLoading ? (
            <TableSkeleton />
          ) : (
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: theme.customColors?.text?.strong || "#111827",
                  mb: 2,
                }}
              >
                Risks ({risks.length})
              </Typography>
              <RiskAssessmentTable
                risks={risks}
                onViewDetails={handleViewDetails}
                refetch={refetchRisks}
                enableHorizontalScroll
              />
            </Box>
          )}
        </Stack>
      </CardContent>

      {/* Risk Details Dialog */}
      {selectedRisk && (
        <RiskDetailsDialog
          open={detailsDialogOpen}
          onClose={handleDetailsDialogClose}
          risk={selectedRisk}
          assessmentId={currentAssessment?.id}
          auditId={audit?.id}
          isAuditorView={isAuditorView}
        />
      )}
    </Card>
  );
};

export default RiskAssessmentsTab;
